import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { getLearnResources, createLearnResource, updateLearnResource, deleteLearnResource } from '../../api/api';

function Learn() {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  const [formData, setFormData] = useState({
    id: null,
    title: '',
    description: '',
    category: '',
    url: ''
  });

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    setLoading(true);
    try {
      const response = await getLearnResources();
      setResources(response.data.data);
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error loading resources');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'success', message: '' }), 5000);
  };

  const handleOpenDialog = (resource = null) => {
    if (resource) {
      setFormData({
        id: resource.id,
        title: resource.title,
        description: resource.description,
        category: resource.category,
        url: resource.url || ''
      });
    } else {
      setFormData({
        id: null,
        title: '',
        description: '',
        category: '',
        url: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveResource = async () => {
    try {
      if (formData.id) {
        await updateLearnResource(formData.id, formData);
        showAlert('success', 'Resource updated successfully');
      } else {
        await createLearnResource(formData);
        showAlert('success', 'Resource created successfully');
      }
      handleCloseDialog();
      loadResources();
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error saving resource');
    }
  };

  const handleDeleteResource = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await deleteLearnResource(id);
        showAlert('success', 'Resource deleted successfully');
        loadResources();
      } catch (error) {
        showAlert('error', error.response?.data?.error || 'Error deleting resource');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('learn.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('learn.addResource')}
        </Button>
      </Box>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('learn.resourcesList')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : resources.length === 0 ? (
            <Typography color="text.secondary" align="center" p={3}>
              {t('learn.noResources')}
            </Typography>
          ) : (
            <List>
              {resources.map((resource) => (
                <ListItem key={resource.id} divider>
                  <ListItemText
                    primary={resource.title}
                    secondary={`${resource.category} - ${resource.description}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleOpenDialog(resource)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDeleteResource(resource.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formData.id ? t('learn.editResource') : t('learn.addResource')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('learn.title')}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('learn.description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('learn.category')}
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('learn.url')}
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('learn.cancel')}</Button>
          <Button
            onClick={handleSaveResource}
            variant="contained"
            disabled={!formData.title || !formData.description || !formData.category}
          >
            {t('learn.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Learn;